import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { CheckCircle, XCircle } from 'lucide-react';
import LoginButton from '../LoginButton';
import { Logo } from '../App';
import { api } from '../api';

const STORAGE_KEY = 'pendingPairToken';

// Reachable at /pair?token=... (the URL encoded in the QR shown on the TV).
// If the user isn't signed in we stash the token, send them through Auth0, and
// resume the claim when they return.
export default function PairView({ onDone }) {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const [status, setStatus] = useState('working'); // working | success | error | needauth
  const [message, setMessage] = useState('Pairing your TV…');
  // The auth0 `user` object gets a new identity across renders, which re-runs
  // the claim effect — a second claim of the same token 409s ("Already
  // paired") and would stomp the success screen. Claim exactly once.
  const claimAttempted = useRef(false);

  // Capture the token ONCE. It's recomputed state otherwise: after a claim
  // succeeds we clear the stash, a re-render would see token=null, and the
  // "no token" branch below would stomp the success screen.
  const [token] = useState(() =>
    new URLSearchParams(window.location.search).get('token') ||
    localStorage.getItem(STORAGE_KEY)
  );

  useEffect(() => {
    if (token) localStorage.setItem(STORAGE_KEY, token);
  }, [token]);

  useEffect(() => {
    if (isLoading) return;
    if (claimAttempted.current) return; // terminal state reached — never regress
    if (!token) {
      // Reached /pair without a code — usually browser autocomplete/history
      // rather than a QR scan. Guide, don't alarm.
      setStatus('error');
      setMessage('This page pairs a TV to your account. Point your phone’s camera at the QR code on the TV screen — that link brings you back here with the pairing code attached.');
      return;
    }
    if (!isAuthenticated) { setStatus('needauth'); setMessage('Sign in to finish pairing this TV.'); return; }
    claimAttempted.current = true;

    (async () => {
      try {
        await api.claimPairing(token, user.sub, user.email);
        localStorage.removeItem(STORAGE_KEY);
        setStatus('success');
        setMessage('TV paired! It will show your content shortly.');
      } catch (e) {
        // A stale token must not haunt the next scan.
        localStorage.removeItem(STORAGE_KEY);
        setStatus('error');
        const msg = e.message || 'Pairing failed.';
        if (/expired|unknown token/i.test(msg)) {
          setMessage("That pairing code expired — the TV is already showing a fresh QR. Scan the new one; you'll stay signed in this time.");
        } else if (/already paired/i.test(msg)) {
          setMessage('This TV is already paired to an account.');
        } else {
          setMessage(msg);
        }
      }
    })();
  }, [isLoading, isAuthenticated, token, user]);

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">
          <Logo size={34} />
          <span className="brand-name">Pair TV</span>
        </div>
        {status === 'working' && <p className="tagline">{message}</p>}
        {status === 'needauth' && (
          <>
            <p className="tagline">{message}</p>
            <LoginButton />
          </>
        )}
        {status === 'success' && (
          <>
            <div className="status-icon"><CheckCircle size={44} color="var(--ok)" /></div>
            <p className="tagline">{message}</p>
            <button className="btn btn-primary" onClick={onDone}>Go to dashboard</button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="status-icon"><XCircle size={44} color="var(--danger)" /></div>
            <p className="tagline">{message}</p>
            <button className="btn btn-ghost" onClick={onDone}>Back to dashboard</button>
          </>
        )}
      </div>
    </div>
  );
}
